import { useEffect, useState } from "react";
import api from "@/lib/api";

/**
 * useHeaderNav — shared admin-controlled header nav across <Header> and <Footer>.
 *
 * Why this hook exists:
 *   Footer used to hard-code its Shop / Collections columns. When admin
 *   disabled a header nav item (e.g. "Sell Designs"), it disappeared from
 *   the top bar but stayed visible in the footer — confusing, and a real
 *   bug reported by the user.
 *
 *   Now both components read from the same source. The fetch happens once
 *   per page load (deferred to idle so it never competes with LCP), then
 *   cached at the module level so Header + Footer mounting in the same
 *   tree don't duplicate the network call.
 *
 * Shape:
 *   items: array of { id, label, href, icon, style, is_visible }
 *          (only visible items — the public `/header-nav` endpoint already
 *          filters out is_visible=false rows).
 *   isHrefVisible(href): false when the href is a "managed" header-nav slot
 *          AND not present in the API response (admin hid it). True for any
 *          unmanaged href (footer-only links like /about, /terms etc.) so
 *          we don't accidentally nuke the entire footer.
 */

// Mirrors the seed IDs in /app/backend/routes_header_nav.py. Any href in
// this set is considered "admin-controlled" — if it's missing from the API
// response, the corresponding footer link should hide. Anything outside this
// set (e.g. /about, /terms, /collections/*) is unmanaged and always visible.
const MANAGED_HREFS = new Set([
  "/shop",
  "/custom-shop",
  "/couple-wear",
  "/heavy-duty",
  "/sell-your-designs",
  "/leaderboard",
]);

let _cache = null;        // last successful response (frozen)
let _inflight = null;     // pending fetch promise (dedupes concurrent callers)

function fetchOnce() {
  if (_cache) return Promise.resolve(_cache);
  if (_inflight) return _inflight;
  _inflight = api.get("/header-nav")
    .then((r) => {
      _cache = Array.isArray(r.data) ? r.data : [];
      return _cache;
    })
    .catch(() => {
      // Keep _cache as null so a later retry can succeed; consumers fall
      // back to their own hard-coded defaults until that happens.
      return [];
    })
    .finally(() => { _inflight = null; });
  return _inflight;
}

export function useHeaderNav() {
  const [items, setItems] = useState(_cache || []);
  const [loaded, setLoaded] = useState(_cache !== null);

  useEffect(() => {
    if (_cache) {
      setLoaded(true);
      return;
    }
    const run = () => fetchOnce().then((data) => {
      if (Array.isArray(data)) setItems(data);
      setLoaded(true);
    });
    if (typeof window !== "undefined" && "requestIdleCallback" in window) {
      const id = window.requestIdleCallback(run, { timeout: 2500 });
      return () => { try { window.cancelIdleCallback(id); } catch { /* noop */ } };
    }
    const t = setTimeout(run, 800);
    return () => clearTimeout(t);
  }, []);

  // Build a fast href-based set of currently-visible managed hrefs. The API
  // only returns is_visible=true rows so presence == visible.
  const visibleHrefSet = new Set();
  for (const it of items) {
    const href = (it.href || "").replace(/\/$/, "");
    if (href) visibleHrefSet.add(href);
  }

  /**
   * @param {string} href — link target to check
   * @returns {boolean}
   *   - true for any unmanaged href (footer-only legal/collection links)
   *   - true if the response hasn't loaded yet (avoids a flash of missing
   *     footer links before the API resolves)
   *   - otherwise: true iff the managed href is present in the API list
   */
  const isHrefVisible = (href) => {
    if (!href) return true;
    const key = href.replace(/\/$/, "");
    if (!MANAGED_HREFS.has(key)) return true; // unmanaged → always show
    if (!loaded) return true;                 // pre-fetch: don't hide eagerly
    return visibleHrefSet.has(key);
  };

  return { items, isHrefVisible, loaded };
}
